import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ScenarioComparisonMatrix from './components/ScenarioComparisonMatrix';
import RiskHeatMap from './components/RiskHeatMap';
import { getEstimatesFromDB } from '../../services/dataService';
import { getTemplates } from '../../utils/estimateStorage';
import { format, parseISO, subMonths, isWithinInterval } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { useRBAC } from '../../contexts/RBACContext';

const AnalyticsDashboard = () => {
  const { user } = useAuth();
  const { canViewOwn, MODULE_KEYS } = useRBAC();
  const [costTrends, setCostTrends] = useState([]);
  const [templateUsage, setTemplateUsage] = useState([]);
  const [deliveryModeData, setDeliveryModeData] = useState([]);
  const [costBreakdown, setCostBreakdown] = useState([]);
  const [profitMargins, setProfitMargins] = useState([]);
  const [kpiMetrics, setKpiMetrics] = useState(null);
  const [topProjects, setTopProjects] = useState([]);
  const [advancedMetrics, setAdvancedMetrics] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, [refreshKey]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      // Apply View Own filter: only show current user's estimates when view_own only
      const viewOwnOnly = canViewOwn(MODULE_KEYS?.ANALYTICS_DASHBOARD) || canViewOwn(MODULE_KEYS?.SERVICE_CALCULATOR);
      const estimates = await getEstimatesFromDB(viewOwnOnly ? user?.id : null);
      const templates = getTemplates();
      computeAnalytics(estimates, templates);
    } catch (err) {
      console.error('Analytics load error:', err);
      computeAnalytics([], []);
    } finally {
      setLoading(false);
    }
  };

  const computeAnalytics = (estimates, templates) => {
    // --- KPI Metrics ---
    const now = new Date();
    const last30Days = subMonths(now, 1);
    const previous30Days = subMonths(now, 2);

    const recentCount = estimates?.filter(est => {
      try { return isWithinInterval(parseISO(est?.timestamp), { start: last30Days, end: now }); } catch { return false; }
    })?.length;
    const previousCount = estimates?.filter(est => {
      try { return isWithinInterval(parseISO(est?.timestamp), { start: previous30Days, end: last30Days }); } catch { return false; }
    })?.length;

    const totalValue = estimates?.reduce((sum, est) => sum + (est?.calculations?.sellPrice || 0), 0);
    const totalMargin = estimates?.reduce((sum, est) => sum + (est?.calculations?.profitMarginPercentage || 0), 0);
    let recentTrend = 'neutral';
    if (recentCount > previousCount) recentTrend = 'up';
    if (recentCount < previousCount) recentTrend = 'down';

    setKpiMetrics({
      totalEstimates: estimates?.length,
      avgProjectValue: estimates?.length > 0 ? totalValue / estimates?.length : 0,
      avgProfitMargin: estimates?.length > 0 ? totalMargin / estimates?.length : 0,
      totalTemplates: templates?.length || 0,
      recentTrend,
      recentCount,
      previousCount,
    });

    // --- Cost Trends ---
    const monthlyData = {};
    estimates?.forEach(est => {
      try {
        const monthKey = format(parseISO(est?.timestamp), 'MMM yyyy');
        if (!monthlyData?.[monthKey]) monthlyData[monthKey] = { month: monthKey, totalCost: 0, sellPrice: 0, grossProfit: 0, count: 0 };
        monthlyData[monthKey].totalCost += est?.calculations?.totalCost || 0;
        monthlyData[monthKey].sellPrice += est?.calculations?.sellPrice || 0;
        monthlyData[monthKey].grossProfit += est?.calculations?.grossProfit || 0;
        monthlyData[monthKey].count += 1;
      } catch {}
    });
    setCostTrends(Object.values(monthlyData)?.sort((a, b) => new Date(a?.month) - new Date(b?.month)));

    // --- Profit Margin Trends ---
    const monthlyMargins = {};
    estimates?.forEach(est => {
      try {
        const monthKey = format(parseISO(est?.timestamp), 'MMM yyyy');
        if (!monthlyMargins?.[monthKey]) monthlyMargins[monthKey] = { month: monthKey, margins: [] };
        monthlyMargins?.[monthKey]?.margins?.push(est?.calculations?.profitMarginPercentage || 0);
      } catch {}
    });
    setProfitMargins(
      Object.values(monthlyMargins)?.map(item => ({
        month: item?.month,
        avgMargin: item?.margins?.reduce((s, m) => s + m, 0) / item?.margins?.length,
        minMargin: Math.min(...item?.margins),
        maxMargin: Math.max(...item?.margins),
      }))?.sort((a, b) => new Date(a?.month) - new Date(b?.month))
    );

    // --- Delivery Mode Distribution ---
    const dist = { onsite: 0, remote: 0, hybrid: 0 };
    estimates?.forEach(est => { const m = est?.deliveryMode || 'onsite'; dist[m] = (dist?.[m] || 0) + 1; });
    setDeliveryModeData([
      { name: 'Onsite', value: dist?.onsite, color: 'var(--color-primary)' },
      { name: 'Remote', value: dist?.remote, color: 'var(--color-accent)' },
      { name: 'Hybrid', value: dist?.hybrid, color: 'var(--color-success)' },
    ]?.filter(d => d?.value > 0));

    // --- Cost Breakdown ---
    if (estimates?.length > 0) {
      const totals = estimates?.reduce((acc, est) => {
        const c = est?.calculations || {};
        return {
          engineers: acc?.engineers + (c?.totalEngineerCosts || 0),
          materials: acc?.materials + (c?.totalMaterialsCost || 0),
          licenses: acc?.licenses + (c?.totalLicensesCosts || 0),
          overhead: acc?.overhead + (c?.overheadCosts || 0),
          contingency: acc?.contingency + (c?.contingencyCosts || 0),
        };
      }, { engineers: 0, materials: 0, licenses: 0, overhead: 0, contingency: 0 });
      const cnt = estimates?.length;
      setCostBreakdown([
        { name: 'Engineers', value: totals?.engineers / cnt, color: 'var(--color-primary)' },
        { name: 'Materials', value: totals?.materials / cnt, color: 'var(--color-accent)' },
        { name: 'Licenses', value: totals?.licenses / cnt, color: 'var(--color-success)' },
        { name: 'Overhead', value: totals?.overhead / cnt, color: 'var(--color-warning)' },
        { name: 'Contingency', value: totals?.contingency / cnt, color: 'var(--color-error)' },
      ]?.filter(d => d?.value > 0));
    } else {
      setCostBreakdown([]);
    }

    // --- Template Usage ---
    setTemplateUsage(
      templates?.map(t => ({ name: t?.name, usageCount: t?.usageCount || 0 }))
        ?.sort((a, b) => b?.usageCount - a?.usageCount)
        ?.slice(0, 10)
    );

    // --- Top Projects ---
    setTopProjects(
      estimates
        ?.filter(est => est?.name || est?.projectName)
        ?.sort((a, b) => (b?.calculations?.sellPrice || 0) - (a?.calculations?.sellPrice || 0))
        ?.slice(0, 5)
        ?.map(est => ({
          name: est?.name || est?.projectName,
          value: est?.calculations?.sellPrice || 0,
          margin: est?.calculations?.profitMarginPercentage || 0,
          date: est?.timestamp,
        }))
    );

    // --- Advanced Metrics ---
    if (estimates?.length > 0) {
      const last90Days = subMonths(now, 3);
      const margins = estimates?.map(e => e?.calculations?.profitMarginPercentage || 0);
      const avgMargin = margins?.reduce((s, m) => s + m, 0) / margins?.length;
      const avgVariance = margins?.map(m => Math.abs(m - avgMargin))?.reduce((s, v) => s + v, 0) / margins?.length;
      const estimationAccuracy = Math.max(0, 100 - avgVariance * 2);

      const recentEsts = estimates?.filter(est => {
        try { return isWithinInterval(parseISO(est?.timestamp), { start: last90Days, end: now }); } catch { return false; }
      });
      const quoteToAwardRatio = recentEsts?.length > 0
        ? Math.min(100, (recentEsts?.length / Math.max(1, estimates?.length)) * 100 * 1.5)
        : 0;

      const totalEngineerHours = estimates?.reduce((sum, est) => {
        return sum + (est?.selectedEngineers?.reduce((s, eng) => s + ((eng?.count || 0) * (eng?.hours || 0)), 0) || 0);
      }, 0);
      const resourceUtilization = Math.min(100, (totalEngineerHours / estimates?.length / 2000) * 100);

      const recentMargins = recentEsts?.map(e => e?.calculations?.profitMarginPercentage || 0);
      const recentAvgMargin = recentMargins?.length > 0 ? recentMargins?.reduce((s, m) => s + m, 0) / recentMargins?.length : 0;
      const profitabilityTrend = recentAvgMargin > avgMargin ? 'up' : recentAvgMargin < avgMargin ? 'down' : 'neutral';
      const profitabilityChange = Math.abs(recentAvgMargin - avgMargin);

      setAdvancedMetrics({ estimationAccuracy, quoteToAwardRatio, resourceUtilization, profitabilityTrend, profitabilityChange });
    } else {
      setAdvancedMetrics(null);
    }
  };

  const handleRefresh = () => setRefreshKey(prev => prev + 1);

  const formatCurrency = (value) =>
    `OMR ${parseFloat(value || 0)?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`;

  const formatPercent = (value) => `${(value || 0)?.toFixed(1)}%`;

  const CustomTooltip = ({ active, payload, label, formatter }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-popover-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-4 text-xs">
              <span className="text-muted-foreground">{entry?.name}:</span>
              <span className="font-semibold" style={{ color: entry?.color }}>
                {formatter ? formatter(entry?.value) : entry?.value}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const hasData = (kpiMetrics?.totalEstimates || 0) > 0;

  return (
    <MainLayout>
      <div className="p-4 md:p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
              Analytics Dashboard
            </h1>
            <p className="text-sm text-muted-foreground">
              Visualize saved estimates with cost trends, template analytics, and strategic insights
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!loading && !hasData && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-warning/10 text-warning border border-warning/20">
                <Icon name="Info" size={12} />
                No saved estimates yet — create estimates to see real analytics
              </span>
            )}
            <Button onClick={handleRefresh} variant="outline" className="flex items-center gap-2" disabled={loading}>
              <Icon name={loading ? 'Loader' : 'RefreshCw'} size={16} className={loading ? 'animate-spin' : ''} />
              {loading ? 'Loading...' : 'Refresh Data'}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="flex flex-col items-center gap-3">
              <Icon name="Loader" size={32} className="animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading analytics data...</p>
            </div>
          </div>
        ) : !hasData ? (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-muted text-muted-foreground">
              <Icon name="BarChart3" size={32} />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-foreground mb-1">No Data Available</h3>
              <p className="text-sm text-muted-foreground max-w-sm">
                Save estimates in the Service Calculator to populate your analytics dashboard with real data.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-smooth">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground mb-1">Total Estimates</p>
                    <h3 className="text-2xl font-bold text-foreground">{kpiMetrics?.totalEstimates}</h3>
                  </div>
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 text-primary">
                    <Icon name="FileText" size={20} />
                  </div>
                </div>
                {kpiMetrics?.recentTrend !== 'neutral' && (
                  <div className="flex items-center gap-2 pt-3 border-t border-border">
                    <Icon
                      name={kpiMetrics?.recentTrend === 'up' ? 'TrendingUp' : 'TrendingDown'}
                      size={16}
                      className={kpiMetrics?.recentTrend === 'up' ? 'text-success' : 'text-error'}
                    />
                    <span className={`text-xs font-medium ${kpiMetrics?.recentTrend === 'up' ? 'text-success' : 'text-error'}`}>
                      {kpiMetrics?.recentCount} this month
                    </span>
                  </div>
                )}
              </div>

              <div className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-smooth">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground mb-1">Avg Project Value</p>
                    <h3 className="text-2xl font-bold text-foreground">
                      {formatCurrency(kpiMetrics?.avgProjectValue)}
                    </h3>
                  </div>
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-success/10 text-success">
                    <Icon name="DollarSign" size={20} />
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-smooth">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground mb-1">Avg Profit Margin</p>
                    <h3 className="text-2xl font-bold text-foreground">
                      {formatPercent(kpiMetrics?.avgProfitMargin)}
                    </h3>
                  </div>
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-accent/10 text-accent">
                    <Icon name="TrendingUp" size={20} />
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-smooth">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground mb-1">Active Templates</p>
                    <h3 className="text-2xl font-bold text-foreground">{kpiMetrics?.totalTemplates}</h3>
                  </div>
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-warning/10 text-warning">
                    <Icon name="Layers" size={20} />
                  </div>
                </div>
              </div>
            </div>

            {/* Advanced Performance Metrics */}
            {advancedMetrics && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-4 hover:shadow-md transition-smooth">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground mb-1">Estimation Accuracy</p>
                      <h3 className="text-2xl font-bold text-foreground">
                        {advancedMetrics?.estimationAccuracy?.toFixed(1)}%
                      </h3>
                    </div>
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/20 text-primary">
                      <Icon name="Target" size={20} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-3 border-t border-primary/20">
                    <Icon name="Info" size={14} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Margin consistency</span>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-success/10 to-success/5 border border-success/20 rounded-lg p-4 hover:shadow-md transition-smooth">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground mb-1">Quote-to-Award Ratio</p>
                      <h3 className="text-2xl font-bold text-foreground">
                        {advancedMetrics?.quoteToAwardRatio?.toFixed(1)}%
                      </h3>
                    </div>
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-success/20 text-success">
                      <Icon name="Award" size={20} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-3 border-t border-success/20">
                    <Icon name="Info" size={14} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Last 90 days</span>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-accent/10 to-accent/5 border border-accent/20 rounded-lg p-4 hover:shadow-md transition-smooth">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground mb-1">Resource Utilization</p>
                      <h3 className="text-2xl font-bold text-foreground">
                        {advancedMetrics?.resourceUtilization?.toFixed(1)}%
                      </h3>
                    </div>
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-accent/20 text-accent">
                      <Icon name="Users" size={20} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-3 border-t border-accent/20">
                    <Icon name="Info" size={14} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Avg hours allocated</span>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-warning/10 to-warning/5 border border-warning/20 rounded-lg p-4 hover:shadow-md transition-smooth">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground mb-1">Profitability Trend</p>
                      <h3 className="text-2xl font-bold text-foreground">
                        {advancedMetrics?.profitabilityChange?.toFixed(1)}%
                      </h3>
                    </div>
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-warning/20 text-warning">
                      <Icon name="TrendingUp" size={20} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-3 border-t border-warning/20">
                    <Icon
                      name={advancedMetrics?.profitabilityTrend === 'up' ? 'ArrowUp' : advancedMetrics?.profitabilityTrend === 'down' ? 'ArrowDown' : 'Minus'}
                      size={14}
                      className={advancedMetrics?.profitabilityTrend === 'up' ? 'text-success' : advancedMetrics?.profitabilityTrend === 'down' ? 'text-error' : 'text-muted-foreground'}
                    />
                    <span className={`text-xs font-medium ${advancedMetrics?.profitabilityTrend === 'up' ? 'text-success' : advancedMetrics?.profitabilityTrend === 'down' ? 'text-error' : 'text-muted-foreground'}`}>
                      {advancedMetrics?.profitabilityTrend === 'up' ? 'Improving' : advancedMetrics?.profitabilityTrend === 'down' ? 'Declining' : 'Stable'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Scenario Comparison Matrix */}
            <ScenarioComparisonMatrix />

            {/* Risk Heat Map */}
            <RiskHeatMap />

            {/* Cost Trends Chart */}
            {costTrends?.length > 0 && (
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="TrendingUp" size={20} className="text-primary" />
                  Cost Trends Over Time
                </h3>
                <div className="w-full h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={costTrends} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                      <XAxis dataKey="month" tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }} stroke="var(--color-border)" />
                      <YAxis tickFormatter={formatCurrency} tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }} stroke="var(--color-border)" />
                      <Tooltip content={<CustomTooltip formatter={formatCurrency} />} />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                      <Line type="monotone" dataKey="totalCost" stroke="var(--color-primary)" strokeWidth={2} name="Total Cost" dot={{ fill: 'var(--color-primary)', r: 4 }} />
                      <Line type="monotone" dataKey="sellPrice" stroke="var(--color-success)" strokeWidth={2} name="Sell Price" dot={{ fill: 'var(--color-success)', r: 4 }} />
                      <Line type="monotone" dataKey="grossProfit" stroke="var(--color-accent)" strokeWidth={2} name="Gross Profit" dot={{ fill: 'var(--color-accent)', r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Profit Margin Trends */}
            {profitMargins?.length > 0 && (
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="Percent" size={20} className="text-accent" />
                  Profit Margin Trends
                </h3>
                <div className="w-full h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={profitMargins} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                      <XAxis dataKey="month" tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }} stroke="var(--color-border)" />
                      <YAxis tickFormatter={formatPercent} tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }} stroke="var(--color-border)" />
                      <Tooltip content={<CustomTooltip formatter={formatPercent} />} />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                      <Line type="monotone" dataKey="avgMargin" stroke="var(--color-accent)" strokeWidth={2} name="Avg Margin" dot={{ fill: 'var(--color-accent)', r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Delivery Mode Distribution */}
              {deliveryModeData?.length > 0 && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="PieChart" size={20} className="text-success" />
                    Delivery Mode Distribution
                  </h3>
                  <div className="w-full h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={deliveryModeData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100)?.toFixed(0)}%`}
                          outerRadius={80}
                          fill="var(--color-primary)"
                          dataKey="value"
                        >
                          {deliveryModeData?.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry?.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Cost Breakdown */}
              {costBreakdown?.length > 0 && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="BarChart3" size={20} className="text-warning" />
                    Average Cost Breakdown
                  </h3>
                  <div className="w-full h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={costBreakdown} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                        <XAxis dataKey="name" tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }} stroke="var(--color-border)" />
                        <YAxis tickFormatter={formatCurrency} tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }} stroke="var(--color-border)" />
                        <Tooltip content={<CustomTooltip formatter={formatCurrency} />} />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                          {costBreakdown?.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry?.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

            {/* Template Usage Analytics */}
            {templateUsage?.length > 0 && (
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="Layers" size={20} className="text-primary" />
                  Template Usage Analytics
                </h3>
                <div className="w-full h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={templateUsage} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                      <XAxis dataKey="name" tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }} stroke="var(--color-border)" />
                      <YAxis tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }} stroke="var(--color-border)" />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                      <Bar dataKey="usageCount" fill="var(--color-primary)" name="Usage Count" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Top Projects */}
            {topProjects?.length > 0 && (
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="Award" size={20} className="text-success" />
                  Top Projects by Value
                </h3>
                <div className="space-y-3">
                  {topProjects?.map((project, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-4 p-4 bg-gradient-to-r from-muted/50 to-transparent rounded-lg border border-border hover:border-primary/50 transition-smooth"
                    >
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-bold text-sm flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">{project?.name}</p>
                        <p className="text-xs text-muted-foreground">
                          Margin: {formatPercent(project?.margin)}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-bold text-foreground">{formatCurrency(project?.value)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default AnalyticsDashboard;
